import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import authService from '../services/auth.service';

interface ProtectedRouteProps {
  children: React.ReactElement;
}

/**
 * Protected Route Component
 * Checks authentication before rendering protected pages
 * Redirects to login if no valid token
 * 
 * ✅ EXCEPTION: Login pages, signup, and transaction-password pages are ALWAYS allowed
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const token = authService.getToken();
  
  // ✅ CRITICAL: Always allow access to login and public pages (no authentication check)
  const publicPaths = [
    '/login',
    '/admin/login',
    '/affiliate/login',
    '/affilate/login',
    '/dash',
    '/signup',
    '/transaction-password',
    '/admin/transaction-password',
  ];
  
  const isPublicPath = publicPaths.some(path => location.pathname === path || location.pathname.startsWith(path));
  
  // Determine which type of route this is for proper redirects
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAffiliateRoute = location.pathname.startsWith('/affiliate') || location.pathname.startsWith('/affilate');

  useEffect(() => {
    // Clear any cached error states when navigating
    try {
      // Suppress any pending errors from previous page
      window.onerror = null;
      window.onunhandledrejection = null;
      
      // Re-initialize error handlers
      const setupHandlers = require('../setupErrorHandlers').setupGlobalErrorHandlers;
      if (setupHandlers) {
        setupHandlers();
      }
    } catch (e) {
      // Silent fail
    }
  }, [location.pathname]);

  // ✅ If it's a public path (login, signup, etc.), ALWAYS allow access
  if (isPublicPath) {
    console.log('✅ Public path - allowing access without token:', location.pathname);
    return children;
  }

  // ✅ For protected pages, check if token exists
  if (!token) {
    console.log('🔐 No token found - redirecting to login');
    
    // Redirect to appropriate login page based on route type
    if (isAdminRoute) {
      return <Navigate to="/admin/login" replace />;
    } else if (isAffiliateRoute) {
      return <Navigate to="/affilate/login" replace />;
    } else {
      return <Navigate to="/login" replace />;
    }
  }

  // Token exists - render protected content
  return children;
};

export default ProtectedRoute;

